import { useEffect, useRef, useState } from "react";
import { BiSolidShareAlt } from "react-icons/bi";
import { HiMiniPrinter } from "react-icons/hi2";
import { useRouter } from "next/router";
import PropTypes from "prop-types";
import { useContentfulTranslations } from "@/utils/contentfulTranslations";
import { EmailSVG, Fb, Twitter } from "@/components/svgs";
import CompareProducts from "@components/CustomComponents/CompareProducts";
import { useDeviceType } from "@/hooks/useDeviceType";
import EmailFriendModal from "./CustomComponents/EmailAFriend";

export const SocialButton = ({
  href,
  onClick,
  className,
  title,
  icon,
  label,
}) => (
  <li className="relative group hover:bg-[#eee] cursor-pointer">
    <button
      type="button"
      target="popup"
      data-href={href}
      onClick={onClick}
      rel="noreferrer"
      className={`${className} group-hover:bg-[#eee] cursor-pointer`}
      title={title}
    >
      <div className="flex items-center p-[5px] gap-[5px]">
        <div className="flex w-[20px] h-[20px] items-center shrink-0 cursor-pointer">
          {icon}
        </div>
        <div className="text-[#232323] font-[helveticaRoman,_Helvetica,_sans-serif] text-[14.7px] cursor-pointer">
          {label}
        </div>
      </div>
    </button>
  </li>
);
SocialButton.propTypes = {
  href: PropTypes.string,
  onClick: PropTypes.func,
  className: PropTypes.string,
  title: PropTypes.string,
  icon: PropTypes.node,
  label: PropTypes.string,
};

const SocialComponent = ({
  pageType = "",
  pdpCompareData = {},
  emailAFrndPdpData = {},
}) => {
  const router = useRouter();
  const { locale } = router;
  const currentLocale =
    locale === "default"
      ? process.env.NEXT_PUBLIC_DEFAULT_LOCALE
      : locale || "en";
  const { PDP_LABELS, COMPARE_PRODUCTS_DIFF_CATEGORY_ERROR, COMPARE_ERRORS } =
    useContentfulTranslations();
  const ref = useRef();
  const [social, setSocial] = useState(false);
  const [navbarOpen, setNavbarOpen] = useState(() => {
    if (typeof window === "undefined") return false;
    const existing = JSON.parse(localStorage.getItem("compareProducts")) || [];
    return existing.some(
      item =>
        item?.compareMappedValues?.skuId ===
        pdpCompareData?.compareMappedValues?.skuId
    );
  });
  const [emailAFrndModal, setEmailFriendModal] = useState(false);
  const { isMobile } = useDeviceType();

  useEffect(() => {
    const checkIfClickedOutside = e => {
      // If the menu is open and the clicked target is not within the menu,
      // then close the menu
      if (social && ref.current && !ref.current.contains(e.target)) {
        setSocial(false);
      }
    };

    document.addEventListener("mousedown", checkIfClickedOutside);

    return () => {
      // Cleanup the event listener
      document.removeEventListener("mousedown", checkIfClickedOutside);
    };
  }, [social]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const syncNavbarState = () => {
      const existing =
        JSON.parse(localStorage.getItem("compareProducts")) || [];
      const stillExists = existing.some(
        item =>
          item?.compareMappedValues?.skuId ===
          pdpCompareData?.compareMappedValues?.skuId
      );
      setNavbarOpen(stillExists);
    };
    globalThis.addEventListener("storage", syncNavbarState);

    return () => {
      globalThis.removeEventListener("storage", syncNavbarState);
    };
  }, [pdpCompareData?.compareMappedValues?.skuId]);

  const shareOnFacebook = () => {
    globalThis.open(
      `http://www.facebook.com/sharer/sharer.php?u=${globalThis?.location?.href}`,
      "facebook share",
      "width=600,height=400"
    );
  };

  const shareOnTwitter = () => {
    globalThis.open(
      `http://twitter.com/share?text=${
        PDP_LABELS?.[currentLocale]?.twitterText
      }&url=${globalThis?.location?.href}`,
      "twitter share",
      "width=600,height=400"
    );
  };

  const addToCompare = () => {
    if (typeof window === "undefined") return false;

    let existing = JSON.parse(localStorage.getItem("compareProducts")) || [];

    const alreadyExists = existing.some(
      item =>
        item?.compareMappedValues?.skuId ===
        pdpCompareData?.compareMappedValues?.skuId
    );

    // Check for different category (productType)
    if (
      existing.length > 0 &&
      !alreadyExists &&
      existing.some(
        item =>
          item?.compareMappedValues?.["Product Type"] !==
          pdpCompareData?.compareMappedValues?.["Product Type"]
      )
    ) {
      if (
        !window.confirm(COMPARE_PRODUCTS_DIFF_CATEGORY_ERROR?.[currentLocale])
      ) {
        // Cancelled, do not add
        return false;
      }
      // Remove all and add new one
      existing = [pdpCompareData];
      localStorage.setItem("compareProducts", JSON.stringify(existing));
      globalThis.dispatchEvent(new Event("storage"));
      return true;
    }

    if (!alreadyExists) {
      if (existing.length < 3) {
        existing.push(pdpCompareData);
      } else {
        alert(COMPARE_ERRORS[currentLocale]);
        return false;
      }
    }

    localStorage.setItem("compareProducts", JSON.stringify(existing));
    globalThis.dispatchEvent(new Event("storage"));
    return true;
  };

  const removeFromCompare = () => {
    if (typeof window === "undefined") return;
    let existing = JSON.parse(localStorage.getItem("compareProducts")) || [];
    existing = existing.filter(
      item =>
        item?.compareMappedValues?.skuId !==
        pdpCompareData?.compareMappedValues?.skuId
    );
    localStorage.setItem("compareProducts", JSON.stringify(existing));
    globalThis.dispatchEvent(new Event("storage"));
  };

  const handleCompareComponent = () => {
    if (navbarOpen) {
      removeFromCompare();
      setNavbarOpen(false);
    } else if (addToCompare()) {
      setNavbarOpen(true);
    }
  };

  return (
    <>
      {pageType === "pdp" && (
        <CompareProducts navbarOpen={navbarOpen} compareData={pdpCompareData} />
      )}
      {emailAFrndModal && (
        <EmailFriendModal
          emailAFrndModal={true}
          onClose={() => setEmailFriendModal(false)}
          pdpData={emailAFrndPdpData}
          locale={currentLocale}
        />
      )}
      <div className="flex mt-5 mb-5">
        {pageType === "pdp" && !isMobile && (
          <button
            type="button"
            className="print:hidden flex uppercase justify-center items-center mr-3 h-[40px] bg-[#e5e5e5] text-[#232323] rounded hover:bg-[#bebebe] hover:text-[#232323] py-[13px] px-[20px] text-[13px] font-[helveticaBold,_Helvetica,_sans-serif] font-[800] cursor-pointer"
            onClick={handleCompareComponent}
          >
            {navbarOpen ? "-" : "+"}{" "}
            {PDP_LABELS?.[currentLocale]?.compareLabel || "Compare"}
          </button>
        )}
        {pageType === "pdp" && !isMobile ? (
          <>
            <button
              type="button"
              className="print:hidden flex justify-center items-center mr-3 h-[40px] w-[40px] bg-[#e5e5e5] text-[#232323] rounded hover:bg-[#364573] hover:text-[#fff] hover:cursor-pointer"
              onClick={() => setSocial(true)}
              aria-label="Share Link"
            >
              <BiSolidShareAlt size={18} />
            </button>
            <button
              type="button"
              className={`print:hidden flex justify-center items-center mr-3 h-[40px] w-[40px] bg-[#e5e5e5] text-[#232323] rounded hover:bg-[#364573] hover:text-[#fff] hover:cursor-pointer ${pageType === "pdp" && isMobile ? "hidden" : ""}`}
              onClick={() => globalThis.print()}
              aria-label="Print"
            >
              <HiMiniPrinter size={20} />
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className={`print:hidden flex justify-center items-center mr-3 h-[40px] w-[40px] bg-[#e5e5e5] text-[#232323] rounded hover:bg-[#364573] hover:text-[#fff] hover:cursor-pointer ${pageType === "pdp" && isMobile ? "hidden" : ""}`}
              onClick={() => globalThis.print()}
              aria-label="Print"
            >
              <HiMiniPrinter size={20} />
            </button>
            <button
              type="button"
              className="print:hidden flex justify-center items-center mr-3 h-[40px] w-[40px] bg-[#e5e5e5] text-[#232323] rounded hover:bg-[#364573] hover:text-[#fff] hover:cursor-pointer"
              onClick={() => setSocial(true)}
              aria-label="Share Link"
            >
              <BiSolidShareAlt size={18} />
            </button>
          </>
        )}
      </div>
      {social && (
        <div className="ml-20 relative" ref={ref}>
          <ul
            className="flex flex-col cursor-pointer text-[#000] [&_li]:block absolute w-[230px] px-[20px] py-[15px] shadow-md rounded bg-white z-[10]"
            style={{ top: "-20px" }}
          >
            <SocialButton
              href={`http://www.facebook.com/sharer/sharer.php?u=${globalThis?.location?.href}`}
              onClick={shareOnFacebook}
              className="flex bg-[#fff] text-[15px] text-[#325a90] transititext-primary hover:text-opacity-70 hover:no-underline"
              title="Share On facebook"
              icon={<Fb />}
              label="Facebook"
            />
            <SocialButton
              href={`http://twitter.com/share?text=${
                PDP_LABELS?.[currentLocale]?.twitterText
              }&url=${globalThis?.location?.href}`}
              onClick={shareOnTwitter}
              className="flex bg-[#fff] text-[15px] text-[#55acee] transititext-primary hover:text-opacity-70 hover:no-underline"
              title="Share On twitter"
              icon={<Twitter />}
              label="Twitter"
            />
            {pageType === "pdp" ? (
              <SocialButton
                icon={<EmailSVG />}
                label="Email a friend"
                className="flex bg-[#fff] text-[15px] text-[#55acee] transititext-primary hover:text-opacity-70 hover:no-underline"
                onClick={() => setEmailFriendModal(true)}
              />
            ) : null}
          </ul>
        </div>
      )}
    </>
  );
};

export default SocialComponent;

SocialComponent.propTypes = {
  pageType: PropTypes.string,
  pdpCompareData: PropTypes.object,
};
